import { useMemo, useCallback } from 'react';
import type { PrimaryCountryISO } from '@/constants/countries';
import { useTheme } from '@simplywallst/ui-core';
import { MinimalDropdown } from '@/components/Dropdown';
import { getFlagCharacter } from '@utilities/getFlagCharacter';
import useWindowSize from '@/hooks/useWindowSize';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

interface MarketOption {
  option: PrimaryCountryISO | string;
  href?: string;
  label: string;
}

type IndustryOption = {
  option: string;
  label: string;
};

interface Props {
  marketOptions: MarketOption[];
  industryOptions: IndustryOption[];
  selectedMarket: string;
  selectedIndustry: string;
  hideIndustryFilter?: boolean;
  hideMarketFilter?: boolean;
  hideAdvancedFilters?: boolean;
  onMarketChange: (market: string) => void;
  onIndustryChange: (industry: string) => void;
  onAdvancedFilterClick: () => void;
}

export const MarketIndustryFilter = ({
  marketOptions,
  industryOptions,
  selectedMarket,
  selectedIndustry,
  hideIndustryFilter,
  hideMarketFilter,
  onMarketChange,
  onIndustryChange,
}: Props) => {
  const theme = useTheme();
  const { width: screenWidth = 0 } = useWindowSize();
  const { isDesktop, os } = useUserAgent();
  const isLargeScreen =
    screenWidth === 0 ? isDesktop : screenWidth > theme.mdMinRaw;
  const countryFlag =
    os === 'Windows'
      ? ''
      : getFlagCharacter(selectedMarket as PrimaryCountryISO);
  const marketLabel = useMemo(
    () =>
      selectedMarket === 'global'
        ? '🌎\u00A0 Global'
        : `${countryFlag}\u00A0 ${selectedMarket.toUpperCase()} ${
            isLargeScreen ? 'Market' : ''
          }`,
    [selectedMarket, countryFlag, isLargeScreen]
  );
  const industryLabel = useCallback((): string => {
    if (industryOptions && industryOptions.length > 0) {
      const currentIndustry = industryOptions.find(
        (i) => i.option === selectedIndustry
      );
      if (currentIndustry && currentIndustry.label !== 'Any') {
        return currentIndustry.label;
      }
    }
    return 'Industry';
  }, [selectedIndustry, industryOptions]);
  return (
    <>
      {!hideMarketFilter && (
        <MinimalDropdown
          canSearch
          btnProps={{
            display: 'block',
          }}
          name="basic-filter-market"
          options={marketOptions}
          onSelect={onMarketChange}
          value={selectedMarket}
          autoComplete="off"
          placeholder="Search e.g. United States"
          label={marketLabel}
        />
      )}

      {!hideIndustryFilter && (
        <MinimalDropdown
          btnProps={{
            display: 'block',
          }}
          canSearch
          options={industryOptions}
          onSelect={onIndustryChange}
          value={selectedIndustry}
          name="basic-filter-industry"
          autoComplete="off"
          placeholder="Search e.g. Automobiles"
          label={industryLabel()}
        />
      )}
    </>
  );
};
